<template>
   <div>
      <navbar-component />
      <main>
         <!-- Hero: outcome-first for frac ops buyers -->
         <div
            class="relative pt-16 pb-32 flex content-center items-center justify-center min-header"
         >
            <div
               class="absolute top-0 w-full h-full bg-center bg-cover"
               :style="{
                  backgroundImage: `url(${headerImage})`,
               }"
            >
               <span
                  id="blackOverlay"
                  class="w-full h-full absolute opacity-75 bg-black"
               ></span>
            </div>

            <div class="container relative mx-auto">
               <div class="items-center flex flex-wrap">
                  <div class="w-full lg:w-8/12 px-4 ml-auto mr-auto text-center">
                     <div class="pt-8">
                        <h1 class="text-white font-semibold text-4xl md:text-5xl mb-3 leading-tight">
                           Frac control you can trust
                        </h1>
                        <p class="text-white font-medium text-2xl md:text-3xl mb-6 leading-snug text-blueGray-100">
                           on the pad and across the fleet
                        </p>
                        <p class="mt-4 text-lg text-blueGray-200 max-w-3xl mx-auto">
                           Advanced Measurements (AMI) has delivered frac controls
                           and instrumentation for over 25 years—helping pressure
                           pumping teams run safer stages, catch issues sooner, and
                           upgrade without ripping out the fleet.
                        </p>
                        <div class="mt-8 flex flex-wrap gap-3 justify-center">
                           <a
                              href="#contact"
                              class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                           >
                              Contact AMI
                           </a>
                           <router-link
                              to="/mpc"
                              class="inline-block bg-transparent hover:bg-blueGray-800 text-white font-bold py-3 px-6 rounded-lg border border-blueGray-400 transition-colors duration-200"
                           >
                              See Datavan MPC
                           </router-link>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
            <div
               class="top-auto bottom-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden h-70-px"
               style="transform: translateZ(0)"
            >
               <svg
                  class="absolute bottom-0 overflow-hidden"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                  version="1.1"
                  viewBox="0 0 2560 98"
                  x="0"
                  y="0"
               >
                  <polygon
                     class="text-blueGray-200 fill-current"
                     points="2560 0 2560 100 0 100"
                  ></polygon>
               </svg>
            </div>
         </div>

         <section class="pb-12 bg-blueGray-200 -mt-24">
            <div class="container mx-auto px-4">
               <!-- Early credibility for skeptical ops buyers -->
               <div
                  class="relative flex flex-wrap justify-center text-center bg-white shadow-lg rounded-lg mb-8 px-2 py-5"
               >
                  <div
                     v-for="stat in proofStats"
                     :key="stat.label"
                     class="w-1/2 md:w-1/4 px-3 py-2"
                  >
                     <p class="text-2xl font-semibold text-blueGray-800">
                        {{ stat.value }}
                     </p>
                     <p class="text-sm text-blueGray-500 mt-1">{{ stat.label }}</p>
                  </div>
               </div>

               <!-- Light path chooser by buyer job -->
               <div class="flex flex-wrap justify-center gap-3 mb-10 px-2">
                  <router-link
                     v-for="path in buyerPaths"
                     :key="path.label"
                     :to="path.to"
                     class="inline-flex items-center bg-white hover:bg-blueGray-50 text-blueGray-700 font-semibold text-sm py-2 px-4 rounded-lg shadow transition-colors duration-200"
                  >
                     <i :class="[path.icon, 'mr-2 text-blueGray-500']"></i>
                     {{ path.label }}
                  </router-link>
               </div>

               <!-- Job-shaped product cards — top-aligned row -->
               <div class="flex flex-wrap items-stretch">
                  <div class="w-full md:w-4/12 px-4 text-center flex">
                     <div
                        class="relative flex flex-col min-w-0 break-words bg-blueGray-800 border border-blueGray-700 w-full mb-8 shadow-lg rounded-lg"
                     >
                        <div class="px-4 py-5 flex-auto flex flex-col">
                           <div
                              class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-red-400 mx-auto"
                           >
                              <i class="fas fa-sliders-h"></i>
                           </div>
                           <h6 class="text-xl font-semibold text-white">
                              WaveCommand
                           </h6>
                           <p class="mt-2 mb-4 text-blueGray-300 flex-grow">
                              Our new platform for the data van—treatment plans,
                              full MPC, legacy equipment control, plus
                              PumpLink7 and BlenderLink7.
                           </p>
                           <div>
                              <router-link
                                 to="/wavecommand7"
                                 class="text-lightBlue-300 hover:text-lightBlue-200 underline mr-3"
                              >
                                 WaveCommand7
                              </router-link>
                              <router-link
                                 to="/mpc"
                                 class="text-lightBlue-300 hover:text-lightBlue-200 underline mr-3"
                              >
                                 Datavan MPC
                              </router-link>
                              <router-link
                                 to="/pumplink7"
                                 class="text-lightBlue-300 hover:text-lightBlue-200 underline mr-3"
                              >
                                 PumpLink7
                              </router-link>
                              <router-link
                                 to="/blenderlink7"
                                 class="text-lightBlue-300 hover:text-lightBlue-200 underline"
                              >
                                 BlenderLink7
                              </router-link>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="w-full md:w-4/12 px-4 text-center flex">
                     <div
                        class="relative flex flex-col min-w-0 break-words bg-blueGray-800 border border-blueGray-700 w-full mb-8 shadow-lg rounded-lg"
                     >
                        <div class="px-4 py-5 flex-auto flex flex-col">
                           <div
                              class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-lightBlue-400 mx-auto"
                           >
                              <i class="fas fa-cloud text-xl"></i>
                           </div>
                           <h6 class="text-xl font-semibold text-white">
                              WaveData Cloud
                           </h6>
                           <p class="mt-2 mb-4 text-blueGray-300 flex-grow">
                              Centralize sensor channel alarms and remote monitoring
                              across well sites—so supervisors catch issues before
                              the next pad visit turns into downtime.
                           </p>
                           <div>
                              <router-link
                                 to="/wavedata"
                                 class="text-lightBlue-300 hover:text-lightBlue-200 underline"
                              >
                                 Explore WaveData Cloud
                              </router-link>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="w-full md:w-4/12 px-4 text-center flex">
                     <div
                        class="relative flex flex-col min-w-0 break-words bg-blueGray-800 border border-blueGray-700 w-full mb-8 shadow-lg rounded-lg"
                     >
                        <div class="px-4 py-5 flex-auto flex flex-col">
                           <div
                              class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-emerald-400 mx-auto"
                           >
                              <i class="fas fa-microchip"></i>
                           </div>
                           <h6 class="text-xl font-semibold text-white">
                              Control Integrations
                           </h6>
                           <p class="mt-2 mb-4 text-blueGray-300 flex-grow">
                              We build custom control solutions on WaveEdge—modify
                              existing AMI products or create purpose-built systems
                              when your operation needs something unique.
                           </p>
                           <div>
                              <router-link
                                 to="/waveedge"
                                 class="text-lightBlue-300 hover:text-lightBlue-200 underline mr-3"
                              >
                                 Explore WaveEdge
                              </router-link>
                              <a
                                 href="#contact"
                                 class="text-lightBlue-300 hover:text-lightBlue-200 underline"
                              >
                                 Talk about a custom build
                              </a>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <!-- ICP-voiced product insights -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4">
               <news-carousel />
            </div>
         </section>

         <!-- Field support proof (replaces soft "pleasure" copy) -->
         <section class="pb-20 bg-blueGray-200">
            <div class="container mx-auto px-4">
               <div class="flex flex-wrap items-center pt-16">
                  <div class="w-full md:w-5/12 px-4 mr-auto ml-auto">
                     <div
                        class="text-blueGray-500 p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-6 shadow-lg rounded-full bg-white"
                     >
                        <i class="fas fa-headset text-xl"></i>
                     </div>
                     <h3 class="text-3xl mb-2 font-semibold leading-normal">
                        Field support for frac operations
                     </h3>
                     <p
                        class="text-lg font-light leading-relaxed mt-4 mb-4 text-blueGray-600"
                     >
                        Installation, commissioning, training, parts, and
                        responsive field support—so crews get practical help when
                        something on the pad needs attention.
                     </p>
                     <p
                        class="text-lg font-light leading-relaxed mt-0 mb-4 text-blueGray-600"
                     >
                        Whether you need a part replacement, help diagnosing a
                        field problem, or guidance to get a control change done
                        right, our service team is built for frac operations.
                     </p>
                     <router-link
                        to="/fracsupport"
                        class="font-bold text-blueGray-700 hover:text-blueGray-900 underline"
                     >
                        Frac Support
                     </router-link>
                  </div>

                  <div class="w-full md:w-4/12 px-4 mr-auto ml-auto">
                     <div
                        class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded-lg bg-emerald-500"
                     >
                        <img
                           alt="AMI field technician supporting control equipment"
                           src="@/assets/img/servicesupport.png"
                           class="w-full align-middle rounded-t-lg"
                        />
                        <blockquote class="relative p-8 mb-4">
                           <svg
                              preserveAspectRatio="none"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 583 95"
                              class="absolute left-0 w-full block h-95-px -top-94-px"
                           >
                              <polygon
                                 points="-30,95 583,95 583,65"
                                 class="text-emerald-500 fill-current"
                              ></polygon>
                           </svg>
                           <h4 class="text-xl font-bold text-white">
                              Install through field support
                           </h4>
                           <p class="text-md font-light mt-2 text-white">
                              Dedicated customer service through installation,
                              commissioning, training, and field support.
                           </p>
                        </blockquote>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <!-- Outcomes for fleet/pad operators -->
         <section class="relative py-20">
            <div
               class="bottom-auto top-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden -mt-20 h-20"
               style="transform: translateZ(0)"
            >
               <svg
                  class="absolute bottom-0 overflow-hidden"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                  version="1.1"
                  viewBox="0 0 2560 98"
                  x="0"
                  y="0"
               >
                  <polygon
                     class="text-white fill-current"
                     points="2560 0 2560 100 0 100"
                  ></polygon>
               </svg>
            </div>

            <div class="container mx-auto px-4">
               <div class="items-center flex flex-wrap">
                  <div class="w-full md:w-4/12 ml-auto mr-auto px-4">
                     <img
                        alt="Frac control systems built for productive field operations"
                        class="max-w-full rounded-lg shadow-lg"
                        src="@/assets/img/productive.png"
                     />
                  </div>
                  <div class="w-full md:w-5/12 ml-auto mr-auto px-4">
                     <div class="md:pr-12">
                        <div
                           class="text-emerald-600 p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-6 shadow-lg rounded-full bg-emerald-300"
                        >
                           <i class="fas fa-shield-alt text-xl"></i>
                        </div>
                        <h3 class="text-3xl font-semibold">
                           Upgrade the pad without forklift replacement
                        </h3>
                        <p
                           class="mt-4 text-lg leading-relaxed text-blueGray-500"
                        >
                           Run modern control and cloud monitoring alongside the
                           AMI equipment you already trust—so teams stay productive
                           and focused on stage decisions, not fighting the HMI.
                        </p>
                        <ul class="list-none mt-6">
                           <li class="py-2">
                              <div class="flex items-center">
                                 <div>
                                    <span
                                       class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-emerald-600 bg-emerald-200 mr-3"
                                    >
                                       <i class="fas fa-crosshairs"></i>
                                    </span>
                                 </div>
                                 <div>
                                    <h4 class="text-blueGray-500">
                                       Precise group and individual pump control
                                    </h4>
                                 </div>
                              </div>
                           </li>
                           <li class="py-2">
                              <div class="flex items-center">
                                 <div>
                                    <span
                                       class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-emerald-600 bg-emerald-200 mr-3"
                                    >
                                       <i class="fas fa-desktop"></i>
                                    </span>
                                 </div>
                                 <div>
                                    <h4 class="text-blueGray-500">
                                       Clear data access on pad, multi-screen, or mobile
                                    </h4>
                                 </div>
                              </div>
                           </li>
                           <li class="py-2">
                              <div class="flex items-center">
                                 <div>
                                    <span
                                       class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-emerald-600 bg-emerald-200 mr-3"
                                    >
                                       <i class="fas fa-chart-line"></i>
                                    </span>
                                 </div>
                                 <div>
                                    <h4 class="text-blueGray-500">
                                       Fleet alarms, historians, and actionable insights
                                    </h4>
                                 </div>
                              </div>
                           </li>
                        </ul>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <section class="pb-20 relative block bg-blueGray-800">
            <div
               class="bottom-auto top-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden -mt-20 h-20"
               style="transform: translateZ(0)"
            >
               <svg
                  class="absolute bottom-0 overflow-hidden"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                  version="1.1"
                  viewBox="0 0 2560 98"
                  x="0"
                  y="0"
               >
                  <polygon
                     class="text-blueGray-800 fill-current"
                     points="2560 0 2560 100 0 100"
                  ></polygon>
               </svg>
            </div>

            <div class="container mx-auto px-4 lg:pt-24 lg:pb-64">
               <div class="flex flex-wrap text-center justify-center">
                  <div class="w-full lg:w-6/12 px-4">
                     <h2 class="text-4xl font-semibold text-white">
                        Running a fleet?
                     </h2>
                     <p
                        class="text-lg leading-relaxed mt-4 mb-4 text-blueGray-400"
                     >
                        Whether you're upgrading equipment controls, connecting
                        remote monitoring, or building something unique—our
                        WaveEdge platform makes frac systems safer, more
                        accessible, and more reliable.
                     </p>
                  </div>
               </div>
               <div class="flex flex-wrap mt-12 justify-center">
                  <div class="w-full lg:w-3/12 px-4 text-center">
                     <div
                        class="text-blueGray-800 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center"
                     >
                        <i class="fas fa-medal text-xl"></i>
                     </div>
                     <h6 class="text-xl mt-5 font-semibold text-white">
                        Control platforms
                     </h6>
                     <p class="mt-2 mb-4 text-blueGray-400">
                        Proven frac controls with more than 1,500 frac pumps
                        deployed—built for the pace and risk of the pad.
                     </p>
                  </div>
                  <div class="w-full lg:w-3/12 px-4 text-center">
                     <div
                        class="text-blueGray-800 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center"
                     >
                        <i class="fas fa-poll text-xl"></i>
                     </div>
                     <h5 class="text-xl mt-5 font-semibold text-white">
                        Field technologies
                     </h5>
                     <p class="mt-2 mb-4 text-blueGray-400">
                        WaveEdge, PumpLink7, and the redesigned Datavan MPC—built
                        for the field and connected to WaveData Cloud.
                     </p>
                  </div>
                  <div class="w-full lg:w-3/12 px-4 text-center">
                     <div
                        class="text-blueGray-800 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center"
                     >
                        <i class="fas fa-lightbulb text-xl"></i>
                     </div>
                     <h5 class="text-xl mt-5 font-semibold text-white">
                        Cloud operations
                     </h5>
                     <p class="mt-2 mb-4 text-blueGray-400">
                        Remote access, data historians, reporting, and analytics
                        that keep supervisors ahead of alarms—not chasing them.
                     </p>
                  </div>
               </div>
            </div>
         </section>
         <a name="contact"></a>
         <section
            class="relative block py-24 lg:pt-0 bg-blueGray-800"
            id="contact"
         >
            <div class="container mx-auto px-4">
               <div class="flex flex-wrap justify-center lg:-mt-64 -mt-48">
                  <div class="w-full lg:w-6/12 px-4">
                     <div
                        class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-200"
                     >
                        <div class="flex-auto p-5 lg:p-10">
                           <h4 class="text-2xl font-semibold">
                              Need parts, support, or a quote?
                           </h4>
                           <p
                              class="leading-relaxed mt-1 mb-4 text-blueGray-500"
                           >
                              Complete this form and we will get back to you
                              within 24 hours.
                           </p>
                           <form id="contactForm" @submit.prevent="sendMessage">
                              <div class="relative w-full mb-3 mt-8">
                                 <label
                                    class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                                    for="department"
                                 >
                                    Department
                                 </label>
                                 <select
                                    id="department"
                                    name="department"
                                    v-model="formData.department"
                                    required
                                    class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                 >
                                    <option value="" disabled>
                                       Select a department
                                    </option>
                                    <option value="sales">Sales</option>
                                    <option value="parts">Parts</option>
                                    <option value="support">Support</option>
                                 </select>
                              </div>

                              <div class="mb-3 grid grid-cols-2">
                                 <div class="relative mr-2">
                                    <label
                                       class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                                       for="name"
                                    >
                                       Full Name
                                    </label>
                                    <input
                                       type="text"
                                       id="name"
                                       name="name"
                                       v-model="formData.name"
                                       required
                                       class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                       placeholder="Full Name"
                                    />
                                 </div>

                                 <div class="relative">
                                    <label
                                       class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                                       for="phone"
                                    >
                                       Phone
                                    </label>
                                    <input
                                       type="tel"
                                       id="phone"
                                       name="phone"
                                       v-model="formData.phone"
                                       :required="!formData.email"
                                       pattern="[0-9]{10}|[0-9]{3}-[0-9]{3}-[0-9]{4}|\([0-9]{3}\)[0-9]{3}-[0-9]{4}"
                                       class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                       placeholder="Phone (e.g. 123-456-7890)"
                                    />
                                    <small
                                       class="text-blueGray-500 mt-1 text-xs"
                                       >Either email or phone is required</small
                                    >
                                 </div>
                              </div>

                              <div class="relative w-full mb-3">
                                 <label
                                    class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                                    for="email"
                                 >
                                    Email
                                 </label>
                                 <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    v-model="formData.email"
                                    :required="!formData.phone"
                                    class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                    placeholder="Email"
                                 />
                              </div>

                              <div class="relative w-full mb-3">
                                 <label
                                    class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                                    for="message"
                                 >
                                    Message
                                 </label>
                                 <textarea
                                    id="message"
                                    name="message"
                                    v-model="formData.message"
                                    required
                                    rows="4"
                                    class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                                    placeholder="Type a message..."
                                 />
                              </div>
                              <div class="text-center mt-6">
                                 <button
                                    class="bg-blueGray-800 text-white active:bg-blueGray-600 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                                    type="submit"
                                    :disabled="sending || !isFormValid"
                                 >
                                    {{
                                       sending ? "Sending..." : "Send Message"
                                    }}
                                 </button>
                              </div>
                              <div
                                 id="formResponse"
                                 v-if="formResponse"
                                 class="mt-4 text-center"
                                 :class="{
                                    'text-green-500': formSuccess,
                                    'text-red-500': !formSuccess,
                                 }"
                              >
                                 {{ formResponse }}
                              </div>
                           </form>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import NewsCarousel from "@/components/NewsCarousel.vue";
import headerImage from "@/assets/img/industrial-engineer-programming-machine-control.jpg";

export default {
   data() {
      return {
         headerImage,
         // Aligned with About: 25+ years frac, 2,500 systems, 1,500 pumps
         proofStats: [
            { value: "25+", label: "Years frac controls" },
            { value: "1,500+", label: "Frac pumps deployed" },
            { value: "2,500+", label: "Control systems" },
            { value: "1989", label: "Established in Calgary" },
         ],
         buyerPaths: [
            { label: "Fleet monitoring", to: "/wavedata", icon: "fas fa-cloud" },
            { label: "Equipment control", to: "/mpc", icon: "fas fa-sliders-h" },
            {
               label: "Support / parts",
               to: "/fracsupport",
               icon: "fas fa-phone",
            },
         ],
         sending: false,
         formResponse: "",
         formSuccess: false,
         formData: {
            name: "",
            email: "",
            phone: "",
            message: "",
            department: "",
         },
         // Use environment variable for the function URL
         // For Vue CLI:
         functionUrl:
            process.env.VUE_APP_CONTACT_FORM_FUNCTION_URL ||
            "https://contactformemail-gechccb7erftd0g6.canadacentral-01.azurewebsites.net/api/ContactForm?code=Ess0eudzvC6d05Lmq6bib5kHrq7byVX-CVqPg--AF7RKAzFueSEPdA==",
         // For Vite:
         // functionUrl: import.meta.env.VITE_CONTACT_FORM_FUNCTION_URL,
      };
   },
   components: {
      NavbarComponent,
      FooterComponent,
      NewsCarousel,
   },
   mounted() {
      document.title = "Advanced Measurements | AMI";
   },
   computed: {
      isFormValid() {
         // Basic validation: name and message are always required,
         // and at least one of email or phone must be present.
         // Department is now also required.
         return (
            this.formData.name.trim() !== "" &&
            this.formData.message.trim() !== "" &&
            this.formData.department.trim() !== "" &&
            (this.formData.email.trim() !== "" ||
               this.formData.phone.trim() !== "")
         );
      },
   },
   methods: {
      async sendMessage() {
         // Client-side validation: ensure at least email or phone is provided
         // This is good for immediate user feedback, but server-side validation
         // is always essential.
         if (!this.formData.email && !this.formData.phone) {
            this.formResponse =
               "Please provide either an email address or phone number.";
            this.formSuccess = false;
            return;
         }

         // Also check other required fields (name, message, department)
         if (
            !this.formData.name ||
            !this.formData.message ||
            !this.formData.department
         ) {
            this.formResponse = "Please fill in all required fields.";
            this.formSuccess = false;
            return;
         }

         this.sending = true;
         this.formResponse = ""; // Clear previous messages
         this.formSuccess = false;

         try {
            console.log("Sending request to:", this.functionUrl);
            console.log("Request payload:", {
               name: this.formData.name,
               email: this.formData.email,
               phone: this.formData.phone,
               message: this.formData.message,
               department: this.formData.department,
            });

            const response = await fetch(this.functionUrl, {
               method: "POST",
               headers: {
                  "Content-Type": "application/json",
               },
               body: JSON.stringify({
                  name: this.formData.name,
                  email: this.formData.email,
                  phone: this.formData.phone, // Pass phone number
                  message: this.formData.message,
                  department: this.formData.department,
                  // You might want to add a 'subject' field to your form and pass it here
                  // subject: this.formData.subject,
               }),
            });

            console.log("Response status:", response.status);
            console.log(
               "Response headers:",
               Object.fromEntries(response.headers.entries())
            );

            // Check if response has content before trying to parse JSON
            const contentType = response.headers.get("content-type");
            let data;

            if (contentType && contentType.includes("application/json")) {
               data = await response.json();
               console.log("Parsed JSON response:", data);
            } else {
               // Handle non-JSON responses (like empty responses or plain text)
               const textResponse = await response.text();
               console.warn("Non-JSON response received:", textResponse);

               if (response.ok) {
                  this.formResponse = "Message sent successfully!";
                  this.formSuccess = true;

                  // Clear the form only on success
                  this.formData = {
                     name: "",
                     email: "",
                     phone: "",
                     message: "",
                     department: "",
                  };
               } else {
                  this.formResponse = `Server error (${response.status}): ${
                     textResponse || "Unknown error"
                  }`;
                  this.formSuccess = false;
               }
               return;
            }

            if (response.ok && data.success) {
               // Success response
               this.formResponse = data.message || "Submission successful!";
               this.formSuccess = true;

               // Clear the form only on success
               this.formData = {
                  name: "",
                  email: "",
                  phone: "",
                  message: "",
                  department: "",
               };
            } else {
               // Error response
               this.formResponse =
                  data.message || "An error occurred. Please try again.";
               this.formSuccess = false;

               // Log debug info if available
               if (data.debug) {
                  console.error("Server debug info:", data.debug);
               }
            }
         } catch (error) {
            console.error("Error submitting form:", error);
            this.formResponse =
               "A network error occurred. Please try again later.";
            this.formSuccess = false;
         } finally {
            this.sending = false;
         }
      },
   },
};
</script>

<style>
.min-header {
   min-height: 55vh;
}

/* Add some basic styling for your status messages */
.success-message {
   color: green;
}
.error-message {
   color: red;
}
</style>
